// FX display helper (C313): shows an amount in the person's display currency beside its own amount. The rates are display
// only and never stored: the caller passes the rates it has, as units of each currency per one unit of a shared base.
// A missing rate is reported as missing, never guessed.
import { money } from "@offsilo/ui/format";

export type FxRates = Record<string, number>;
export type Amount = { amount: number; currency: string };
export type FxDisplay = { original: string; converted?: string; missingRate?: { from: string; to: string } };

/** Decimal digits a currency uses for display (USD 2, JPY 0). */
export function currencyDigits(code: string): number {
  try {
    return new Intl.NumberFormat("en", { style: "currency", currency: code }).resolvedOptions().maximumFractionDigits ?? 2;
  } catch {
    return 2;
  }
}

/** The amount in `to`, converted through the base. Null when either rate is missing or not positive. */
export function convertAmount(amount: number, from: string, to: string, rates: FxRates): number | null {
  if (from === to) return amount;
  const rateFrom = rates[from];
  const rateTo = rates[to];
  if (rateFrom === undefined || rateTo === undefined || !(rateFrom > 0) || !(rateTo > 0)) return null;
  return amount * (rateTo / rateFrom);
}

/** Rounds to the display currency's digits, so a converted figure never shows more precision than the currency has. */
export function roundToDigits(amount: number, digits: number): number {
  const factor = 10 ** digits;
  return Math.round(amount * factor) / factor;
}

/** What a list shows for one amount: the amount as it is, and, when a display currency is set, the converted amount. */
export function fxDisplay(value: Amount, displayCurrency: string | undefined, rates: FxRates, locale = "en"): FxDisplay {
  const original = money(value.amount, value.currency);
  if (!displayCurrency || displayCurrency === value.currency) return { original };
  const converted = convertAmount(value.amount, value.currency, displayCurrency, rates);
  if (converted === null) return { original, missingRate: { from: value.currency, to: displayCurrency } };
  const digits = currencyDigits(displayCurrency);
  const text = new Intl.NumberFormat(locale, {
    style: "currency",
    currency: displayCurrency,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(roundToDigits(converted, digits));
  return { original, converted: text };
}
